import { useEffect } from "react";

/**
 * Global guard against the browser's default file-drop behavior.
 *
 * By default, when a file is dropped anywhere in the window, the browser
 * navigates to (i.e. opens / previews / downloads) that file, blowing away the
 * SPA. Individual dropzones handle their own `onDrop` and call
 * `stopPropagation()`, so those drops never reach the window listener below.
 *
 * Any drop that is NOT handled by a real dropzone bubbles up to `window`,
 * where we call `preventDefault()` so the browser does nothing instead of
 * opening the file.
 *
 * This must be attached with `capture: false` so component-level `onDrop`
 * handlers (which call `stopPropagation`) can stop propagation before the
 * event reaches the window — allowing legitimate drops to still work.
 */
export function useGlobalDropGuard(): void {
  useEffect(() => {
    const prevent = (e: DragEvent) => {
      e.preventDefault();
    };

    // dragover must also be prevented, otherwise the browser won't fire a
    // valid "drop" event and will fall back to its default navigation.
    window.addEventListener("dragover", prevent);
    window.addEventListener("drop", prevent);

    return () => {
      window.removeEventListener("dragover", prevent);
      window.removeEventListener("drop", prevent);
    };
  }, []);
}
